import "@/settings";
import "./index.css";
import ReactDOM from "react-dom/client";
import { lazy, StrictMode, Suspense } from "react";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router";
import { ToastContainer } from "react-toastify";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { SWRConfig } from "swr";
import Root from "@/components/shared/Root";
import { EditTestLabelView } from "@/components/testlabels/EditTestLabelView";
import { AuthProvider, acquireToken, useAuth } from "@/context/AuthContext";
import { SWRApiError } from "@/hooks/swr";
import { SpecListDetails } from "@/components/specLists/SpecListDetails";
import TestLabelDetails from "@/components/testlabels/TestLabelDetails";
import TestLabelOverview from "@/components/testlabels/TestLabelOverview";
import LandingPage from "./pages/LandingPage";
import ProjectsPage from "./pages/ProjectsPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { RequestPlanningPage } from "./pages/RequestPlanningPage";
import { SpecListPage } from "./pages/SpecListPage";
import { MasterSpecsPage } from "./pages/MasterSpecsPage";
import { DocsIndexPage } from "./pages/docs/DocsIndexPage";
import { CreateProjectDocPage } from "./pages/docs/CreateProjectDocPage";
import { MaskWorkflowDocPage } from "./pages/docs/MaskWorkflowDocPage";
import { CustomSpecListCsvDocPage } from "./pages/docs/CustomSpecListCsvDocPage";
import { PlanMeasurementRequestsDocPage } from "./pages/docs/PlanMeasurementRequestsDocPage";
import { GlossaryDocPage } from "./pages/docs/GlossaryDocPage";
import { ChipPosFileFormatDocPage } from "./pages/docs/ChipPosFileFormatDocPage";
import { PholisDocPage } from "./pages/docs/PholisDocPage";
import { PermissionProvider, ProtectedRoute } from "./permissions";
import { pca } from "./services/msal";
import { FeaturesProvider } from "./context/FeaturesContext";
import TablePageSkeleton from "./components/skeletons/TablePageSkeleton";
import { ChangelogListSkeleton, ChangelogSkeleton } from "./components/skeletons/ChangelogSkeleton";
import { ApplicationContextProvider } from "./context/ApplicationContext";
import { MeasurementRequestDetails } from "./components/measurementRequests/MeasurementRequestDetails";

const ChangelogListPage = lazy(() => import("./pages/ChangelogListPage"));
const ChangelogDetailPage = lazy(() => import("./pages/ChangelogDetailPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const SystemsPage = lazy(() => import("./pages/MeasurementSystemsPage"));
const MeasurementSystemDetailPage = lazy(() => import("./pages/MeasurementSystemsDetailPage"));

const router = createBrowserRouter([
  {
    path: "/",
    Component: Root,
    children: [
      {
        index: true,
        Component: LandingPage,
      },
      {
        path: "/test-labels",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute page="test-labels">
                <TestLabelOverview />
              </ProtectedRoute>
            ),
          },
          {
            path: ":testLabelId/edit",
            element: (
              <ProtectedRoute page="test-labels">
                <EditTestLabelView />
              </ProtectedRoute>
            ),
          },
          {
            path: ":testLabelId",
            element: (
              <ProtectedRoute page="test-labels">
                <TestLabelDetails />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/measurement-requests/:measurementRequestId",
        element: (
          <ProtectedRoute page="measurement-requests">
            <MeasurementRequestDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: "/request-planning",
        element: (
          <ProtectedRoute page="request-planning">
            <RequestPlanningPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/projects",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute page="projects">
                <ProjectsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ":id",
            element: (
              <ProtectedRoute page="projects">
                <ProjectDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/:maskId",
            element: (
              <ProtectedRoute page="projects">
                <ProjectDetailPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/spec-lists",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute page="spec-lists">
                <SpecListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ":id/version/:versionId",
            element: (
              <ProtectedRoute page="spec-lists">
                <SpecListDetails />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/systems",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute page="systems">
                <Suspense fallback={<TablePageSkeleton />}>
                  <SystemsPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: ":id",
            element: (
              <ProtectedRoute page="systems">
                <Suspense fallback={<TablePageSkeleton />}>
                  <MeasurementSystemDetailPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "/master-specs",
        element: (
          <ProtectedRoute page="master-specs">
            <MasterSpecsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/users",
        element: (
          <ProtectedRoute page="users">
            <Suspense fallback={<TablePageSkeleton />}>
              <UsersPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/docs",
        children: [
          {
            index: true,
            Component: DocsIndexPage,
          },
          {
            path: "create-project",
            Component: CreateProjectDocPage,
          },
          {
            path: "mask-workflow",
            Component: MaskWorkflowDocPage,
          },
          {
            path: "custom-spec-list-csv",
            Component: CustomSpecListCsvDocPage,
          },
          {
            path: "plan-measurement-requests",
            Component: PlanMeasurementRequestsDocPage,
          },
          {
            path: "glossary",
            Component: GlossaryDocPage,
          },
          {
            path: "pholis",
            Component: PholisDocPage,
          },
        ],
      },
      {
        path: "/docs/chip-pos-file-format",
        element: <ChipPosFileFormatDocPage />,
      },
      {
        path: "/changelog",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<ChangelogListSkeleton />}>
                <ChangelogListPage />
              </Suspense>
            ),
          },
          {
            path: ":version",
            element: (
              <Suspense fallback={<ChangelogSkeleton />}>
                <ChangelogDetailPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "*",
        id: "catch-all",
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);

// eslint-disable-next-line react-refresh/only-export-components
const RouterWithSWRConfig = () => {
  const { instance, accounts } = useMsal();
  const { setAccessToken } = useAuth();

  return (
    <SWRConfig
      value={{
        onError: async (error) => {
          if (error instanceof SWRApiError) {
            if (error.statusCode === 401) {
              if (!accounts || accounts.length === 0) {
                await instance.logoutRedirect();
              }

              await acquireToken(instance, accounts[0], setAccessToken);
            }
          }
        },
      }}
    >
      <RouterProvider router={router} />
      <ToastContainer theme="colored" limit={2} />
    </SWRConfig>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <StrictMode>
    <MsalProvider instance={pca}>
      <AuthProvider>
        <PermissionProvider>
          <FeaturesProvider>
            <ApplicationContextProvider>
              <RouterWithSWRConfig />
            </ApplicationContextProvider>
          </FeaturesProvider>
        </PermissionProvider>
      </AuthProvider>
    </MsalProvider>
  </StrictMode>,
);
